package org.teddapp.project.ui.habits

import androidx.compose.foundation.border

import org.teddapp.project.ui.teddClickable

import org.teddapp.project.ui.TeddSpace

import org.teddapp.project.ui.TeddFineProgress

import org.teddapp.project.ui.TeddTagTone

import org.teddapp.project.ui.TeddTag

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import org.teddapp.project.ui.DecisionOption
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddConsigne
import org.teddapp.project.ui.TeddDecision
import org.teddapp.project.ui.TeddTokenProgress
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/** Nommer l'habitude : choisir parmi celles déjà observées, ou en nommer une nouvelle en un mot. */
@Composable
internal fun CravingNamingScreen(model: HabitsModel) {
    Dialog(onDismissRequest = model::cancel, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        TeddConsigne(
            eyebrow = "J'ai une envie",
            text = "De quoi as-tu envie, là, maintenant ?",
            modifier = Modifier.fillMaxSize(),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(52.dp)
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd))
                    .padding(horizontal = 16.dp),
                contentAlignment = Alignment.CenterStart,
            ) {
                BasicTextField(
                    value = model.habit,
                    onValueChange = { model.habit = it },
                    singleLine = true,
                    textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink, fontSize = 17.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    modifier = Modifier.fillMaxWidth(),
                    decorationBox = { inner ->
                        if (model.habit.isEmpty()) {
                            Text("Sucre, écrans, cigarette…", color = TeddTokens.InkSubtle, style = TeddType.BodyLg, fontSize = 17.sp)
                        }
                        inner()
                    },
                )
            }
            if (model.knownHabits.isNotEmpty()) {
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    model.knownHabits.take(8).forEach { h -> HabitChip(h, selected = false) { model.pickHabit(h) } }
                }
            }
            TeddButton(
                text = "Continuer",
                onClick = { model.step = CravingStep.Describing },
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Lg,
                enabled = model.canSubmit,
                modifier = Modifier.fillMaxWidth(),
            )
            TeddButton(text = "Annuler", onClick = model::cancel, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
        }
    }
}

/** Décrire les sensations, l'intensité, puis dire ce qui s'est passé. Les deux issues à égalité. */
@Composable
internal fun CravingDescribingScreen(model: HabitsModel) {
    Dialog(onDismissRequest = model::cancel, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        TeddConsigne(
            eyebrow = "Envie · ${model.habit}",
            text = "Où la sens-tu, et à quel point ?",
            centered = false,
            modifier = Modifier.fillMaxSize(),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 84.dp)
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd))
                    .padding(14.dp),
            ) {
                BasicTextField(
                    value = model.sensations,
                    onValueChange = { model.sensations = it },
                    textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink, fontSize = 15.sp, lineHeight = 22.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    modifier = Modifier.fillMaxWidth(),
                    decorationBox = { inner ->
                        if (model.sensations.isEmpty()) {
                            Text("Brûlure dans l'estomac, gorge nouée…", color = TeddTokens.InkSubtle, style = TeddType.BodyLg, fontSize = 15.sp)
                        }
                        inner()
                    },
                )
            }
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                BODY_ZONES.forEach { z -> HabitChip(z, selected = z in model.zones) { model.toggleZone(z) } }
            }
            IntensityRow(value = model.intensity, onChange = { model.intensity = it })
            Text(
                text = "Plus la description est fine, plus l'envie perd de sa force. Rien n'est noté.",
                color = TeddTokens.InkMuted,
                fontSize = 12.5.sp,
            )
            Spacer(modifier = Modifier.height(4.dp))
            TeddDecision(
                first = DecisionOption("J'ai cédé", "c'est un fait, pas un échec"),
                second = DecisionOption("J'ai traversé", "l'envie est passée"),
                onFirst = { model.record(crossed = false) },
                onSecond = { model.record(crossed = true) },
                modifier = Modifier.fillMaxWidth(),
            )
            TeddButton(text = "Annuler", onClick = model::cancel, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
        }
    }
}

/** La consigne somatique : une phrase, beaucoup d'espace, une respiration guidée. */
@Composable
internal fun CravingGuidanceScreen(model: HabitsModel) {
    val (title, body) = somaticGuidance(model.dominantZone)
    Dialog(onDismissRequest = model::finish, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        TeddConsigne(eyebrow = title, text = body, modifier = Modifier.fillMaxSize()) {
            Box(
                modifier = Modifier
                    .size(120.dp)
                    .clip(CircleShape)
                    .background(TeddTokens.LimeSoft, CircleShape)
                    .border(2.dp, TeddTokens.LimeStrong, CircleShape),
            )
            Text("inspire 4 · expire 6", color = TeddTokens.InkMuted, style = TeddType.MonoSm)
            Spacer(modifier = Modifier.height(6.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(28.dp)) {
                TallyNumber("observations", model.totalObservations.toString())
                TallyNumber("traversées", model.totalCrossings.toString())
            }
            TeddButton(
                text = "Terminer",
                onClick = model::finish,
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Lg,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

/** Les compteurs par habitude : les deux montent, toujours. */
@Composable
internal fun HabitTallies(
    tallies: List<HabitTally>,
    modifier: Modifier = Modifier,
    validatedToday: (String) -> Boolean = { false },
    onValidate: ((String) -> Unit)? = null,
) {
    if (tallies.isEmpty()) return
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("PAR HABITUDE", style = TeddType.Overline, color = TeddTokens.InkMuted)
        tallies.forEach { t ->
            val done = validatedToday(t.habit)
            TeddCard(modifier = Modifier.fillMaxWidth()) {
                // Le nom prend sa ligne : accolé aux chiffres, il était comprimé dès que
                // l'habitude portait plus d'un mot.
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = t.habit.replaceFirstChar { it.uppercase() },
                        style = TeddType.Title2,
                        color = TeddTokens.Ink,
                        modifier = Modifier.weight(1f),
                    )
                    if (done) TeddTag("Tenue aujourd'hui", tone = TeddTagTone.Success, icon = "check")
                }

                // Trois nombres de même rang, également espacés : observer, traverser,
                // tenir. Aucun ne prime sur les autres.
                Row(modifier = Modifier.fillMaxWidth().padding(top = 16.dp)) {
                    TallyNumber("observées", t.observations.toString(), Modifier.weight(1f))
                    TallyNumber("traversées", t.crossings.toString(), Modifier.weight(1f))
                    TallyNumber("journées tenues", t.validations.toString(), Modifier.weight(1f))
                }

                // Le désenchantement se voit au lieu de se lire dans une phrase.
                Column(
                    modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            text = "DÉSENCHANTEMENT ESTIMÉ",
                            style = TeddType.Overline,
                            color = TeddTokens.InkMuted,
                            modifier = Modifier.weight(1f),
                        )
                        Text("${t.crossingRate} %", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp)
                    }
                    TeddFineProgress(fraction = t.crossingRate / 100f, height = 8.dp)
                }

                if (onValidate != null && !done) {
                    Row(modifier = Modifier.fillMaxWidth().padding(top = 16.dp)) {
                        TeddButton(
                            text = "J'ai tenu aujourd'hui",
                            onClick = { onValidate(t.habit) },
                            kind = TeddButtonKind.Secondary,
                            size = TeddButtonSize.Md,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TallyNumber(label: String, value: String, modifier: Modifier = Modifier) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(3.dp)) {
        Text(value, style = TeddType.Numeral.copy(fontSize = 24.sp, lineHeight = 28.sp), color = TeddTokens.Ink)
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
    }
}

@Composable
private fun HabitChip(label: String, selected: Boolean, onClick: () -> Unit) {
    org.teddapp.project.ui.TeddFilterChip(label = label, selected = selected, onClick = onClick)
}

/** Intensité de 0 à 10 : dix pastilles, pas un curseur. */
@Composable
private fun IntensityRow(value: Int, onChange: (Int) -> Unit) {
    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Row(modifier = Modifier.weight(1f), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            for (k in 1..10) {
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(22.dp)
                        .clip(CircleShape)
                        .background(if (k <= value) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, CircleShape)
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onChange(k) },
                )
            }
        }
        Text(value.toString(), style = TeddType.Numeral.copy(fontSize = 22.sp, lineHeight = 26.sp), color = TeddTokens.Ink)
    }
}
